import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Check, X, Award, ArrowRight, RotateCw } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { fetchQuiz } from "@/lib/api";
import { toast } from "sonner";

const BADGES_KEY = "lg_reader_badges";
const readBadges = () => { try { return JSON.parse(localStorage.getItem(BADGES_KEY)) || {}; } catch { return {}; } };
const saveBadge = (badge) => {
  const all = readBadges();
  all[badge] = { at: Date.now() };
  localStorage.setItem(BADGES_KEY, JSON.stringify(all));
};

const Quiz = () => {
  const { chapterId } = useParams();
  const nav = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({}); // {qIndex: chosenIndex}
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchQuiz(chapterId)
      .then(setQuiz)
      .catch(() => toast.error("This chapter doesn't have a quiz yet."))
      .finally(() => setLoading(false));
  }, [chapterId]);

  const choose = (qi, ci) => {
    if (submitted) return;
    setAnswers((a) => ({ ...a, [qi]: ci }));
  };

  const submit = () => {
    if (!quiz) return;
    if (Object.keys(answers).length < quiz.questions.length) {
      toast.error("Please answer every question.");
      return;
    }
    setSubmitted(true);
    // Award badge if perfect
    const correct = quiz.questions.filter((q, i) => answers[i] === q.correct_index).length;
    if (correct === quiz.questions.length && quiz.badge) {
      saveBadge(quiz.badge);
      toast.success(`Insight Badge earned: ${quiz.badge}`);
    }
  };

  const reset = () => { setAnswers({}); setSubmitted(false); };

  if (loading) {
    return (
      <main className="min-h-screen bg-cream pt-40 text-center">
        <p className="font-editorial italic text-2xl text-emerald">Loading quiz…</p>
      </main>
    );
  }
  if (!quiz) {
    return (
      <main className="min-h-screen bg-cream pt-40 text-center px-6" data-testid="quiz-empty">
        <h1 className="font-display text-4xl text-emerald mb-3">This chapter has no quiz yet.</h1>
        <p className="font-body text-ink/60 mb-6">Felicia is authoring these — check back soon.</p>
        <Link to={`/read?ch=${chapterId}`} className="btn-outline">Back to the chapter</Link>
      </main>
    );
  }

  const total = quiz.questions.length;
  const correct = quiz.questions.filter((q, i) => answers[i] === q.correct_index).length;
  const perfect = submitted && correct === total;

  return (
    <main className="min-h-screen bg-cream pt-32 pb-24" data-testid="quiz-page">
      <section className="bg-emerald text-cream py-14">
        <div className="max-w-[900px] mx-auto px-6">
          <Reveal>
            <p className="eyebrow text-gold mb-3 flex items-center gap-2">
              <Award className="w-4 h-4" /> {quiz.badge}
            </p>
            <h1 className="font-display text-4xl md:text-5xl leading-tight" data-testid="quiz-title">{quiz.title}</h1>
            <p className="font-editorial italic text-cream/75 text-xl mt-4">
              {total} questions · answer each to earn the Insight Badge.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[900px] mx-auto px-6 pt-14 space-y-10">
        {quiz.questions.map((qq, qi) => {
          const chosen = answers[qi];
          const revealed = submitted;
          return (
            <div key={qi} className="border border-line bg-surface p-6 md:p-8" data-testid={`quiz-question-${qi}`}>
              <p className="eyebrow text-emerald mb-3">
                {qq.type === "mcq" && "Multiple Choice"}
                {qq.type === "tf" && "True or False"}
                {qq.type === "scenario" && "Scenario"}
                <span className="ml-2 text-ink/40">· Question {qi + 1} of {total}</span>
              </p>
              <p className="font-display text-xl md:text-2xl text-ink mb-6">{qq.prompt}</p>
              <div className="space-y-2">
                {qq.choices.map((ch, ci) => {
                  const selected = chosen === ci;
                  const isCorrect = revealed && ci === qq.correct_index;
                  const isWrong = revealed && selected && ci !== qq.correct_index;
                  return (
                    <button
                      key={ci}
                      onClick={() => choose(qi, ci)}
                      disabled={revealed}
                      data-testid={`quiz-q${qi}-choice-${ci}`}
                      className={`w-full text-left p-4 border transition-colors flex items-center gap-3 ${
                        isCorrect ? "bg-emerald text-cream border-emerald"
                        : isWrong ? "bg-red-50 border-red-300 text-red-900"
                        : selected ? "border-emerald bg-cream"
                        : "border-line hover:border-emerald/50"
                      }`}
                    >
                      <span className={`w-5 h-5 shrink-0 border flex items-center justify-center rounded-full ${
                        selected || isCorrect ? "border-current" : "border-ink/30"
                      }`}>
                        {isCorrect && <Check className="w-3 h-3" strokeWidth={3} />}
                        {isWrong && <X className="w-3 h-3" strokeWidth={3} />}
                      </span>
                      <span className="font-body flex-1">{ch}</span>
                    </button>
                  );
                })}
              </div>
              {revealed && qq.explain && (
                <p className="mt-4 p-4 bg-gold/10 border-l-4 border-gold font-body text-sm text-ink/85">
                  <strong className="text-emerald">Why:</strong> {qq.explain}
                </p>
              )}
            </div>
          );
        })}

        {!submitted ? (
          <button onClick={submit} className="btn-emerald w-full justify-center" data-testid="quiz-submit">
            Submit answers <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <div className={`p-8 text-center border ${perfect ? "border-gold bg-gold/10" : "border-line bg-surface"}`}
               data-testid="quiz-result">
            <p className="eyebrow text-emerald mb-2">You scored</p>
            <p className="font-display text-6xl text-emerald mb-3">{correct} <span className="text-ink/30">/</span> {total}</p>
            {perfect ? (
              <>
                <p className="font-editorial italic text-2xl text-emerald mb-4">
                  Insight Badge earned: {quiz.badge}
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <button onClick={() => nav("/read")} className="btn-emerald">Back to the reader</button>
                  <button onClick={reset} className="btn-outline"><RotateCw className="w-4 h-4" /> Retake</button>
                </div>
              </>
            ) : (
              <>
                <p className="font-body text-ink/70 mb-5">
                  Close — read the explanations, then try again to earn the badge.
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <button onClick={reset} className="btn-emerald"><RotateCw className="w-4 h-4" /> Retake</button>
                  <Link to={`/read?ch=${chapterId}`} className="btn-outline">Re-read the chapter</Link>
                </div>
              </>
            )}
          </div>
        )}
      </section>
    </main>
  );
};

export default Quiz;
